import {
  Checkbox,
  Dialog,
  DialogPanel,
  Field,
  Label,
  Radio,
  RadioGroup,
} from "@headlessui/react";
import { computed, type Signal, signal } from "@preact/signals";
import { Show } from "@preact/signals/utils";
import { globalState } from "../../state";
import {
  Button,
  DestructiveButton,
  PrimaryButton,
} from "#palette/components/button";
import { Icons } from "#palette/icons";
import { Progressbar } from "#palette/components/progress";
import { Spinner } from "#palette/components/spinner";

// Questions ///////////////////////////////////////////////////////////////////
// These are the actual questions for the survey- they are defined here for ease of editing as well as more global
// signal management for selected answers.
const selectedAnswer1 = signal("");
const question1Options = ["Yes", "No", "I prefer not to answer."];

// Question 1 is somewhat interesting in that there are effectively sub-options, which are the actual answers reported.
const selectedSubAnswer1 = signal("");
const question1YesSubOptions = [
  "Yes, but my case did not move forward or was unsuccessful.",
  "Yes, and my case was successful.",
  "Yes, and my case is ongoing or I’m not sure the status of my case.",
  "Yes, but I prefer not to give more details.",
];
const question1NoSubOptions = [
  "No, I have not filed a formal report and DON’T plan to.",
  "No, I have not filed a formal report, but DO plan to.",
  "No, I have not filed a formal report, and I’m unsure if I will or not.",
  "No, but I prefer not to give more details.",
];

const selectedAnswer2 = signal([] as string[]);
const question2Options = [
  "To feel less alone.",
  "To help another survivor feel less alone.",
  "To do something, even if I’m not sure or know I don’t want to report.",
  "To create a documented record that this happened.",
  "To support another survivor who might want to pursue further action.",
  "To strengthen my own Title IX or legal case.",
  "To help decide whether or not I want to take action, and what my options are.",
  "To find out if my perpetrator has done it before (or since).",
  "I’m unsure if what happened to me constituted as sexual assault. Matching with someone would make me less unsure.",
  "I prefer not to answer.",
];

const selectedAnswer3 = signal([] as string[]);
const question3Options = [
  "Calm",
  "Hopeful",
  "Empowered/In Control",
  "Exhausted",
  "Worried/Stressed",
  "Overwhelmed",
  "I prefer not to answer.",
];

const selectedAnswer4 = signal("");
const question4Options = [
  "Very Likely",
  "Likely",
  "Not likely",
  "I prefer not to answer.",
];

// Survey Progress /////////////////////////////////////////////////////////////
const currentStep = signal(0);
const progressSegments = signal(4);
const submitting = signal(false);

// We only want to show the progress bar on the actual question pages.
const RenderedProgress = () => {
  const shouldShow = computed(
    () => currentStep.value > 0 && currentStep.value < 5,
  );
  return (
    <Show when={shouldShow}>
      <Progressbar segments={progressSegments} complete={currentStep} />
    </Show>
  );
};

// Survey //////////////////////////////////////////////////////////////////////
export function PostMatchingSurvey(props: {
  open: Signal<boolean>;
  onComplete: () => void;
  onCancel: () => void;
}) {
  const shouldShowEject = computed(() => currentStep.value < 5);

  return (
    <Dialog
      open={props.open.value}
      onClose={() => {}}
      className="fixed inset-0 w-screen items-center justify-center bg-brand-900/20 p-4 transition duration-300 ease-out data-closed:opacity-0 z-50 flex"
    >
      <div className="w-xs lg:w-md max-h-dvh overflow-y-auto">
        <DialogPanel className="mt-5 text-left rounded-lg p-5 flex flex-col gap-2 border border-brand-500 shadow content-start place-self-center bg-brand-0 w-xs lg:w-md">
          <RenderedProgress />
          <Splash />
          <QuestionOne />
          <QuestionTwo />
          <QuestionThree />
          <QuestionFour />
          <Finished onComplete={props.onComplete} />
          <Show when={submitting}>
            <Spinner />
          </Show>
          <Show when={shouldShowEject}>
            <div className="pt-5 text-center">
              <a
                href="#"
                class="color-status-error-fg"
                onClick={(e) => {
                  e.preventDefault();
                  props.onCancel();
                }}
              >
                I'm not interested in taking this survey
              </a>
            </div>
          </Show>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

function Splash() {
  const shouldShow = computed(() => currentStep.value === 0);
  return (
    <Show when={shouldShow}>
      <div>
        <h3
          className="text-lg font-bold font-serif leading-6 capitalize"
          id="modal-title"
        >
          Thank you for entering into Matching. We'd like to ask you a few
          questions to help us understand your experiences with our system.
        </h3>
        <p className="mt-2 text-sm">
          Please know there's no pressure to answer any question you prefer not
          to. There’s no right or wrong answer to these questions. Any response
          you give helps us to empower survivors.
        </p>
        <div className="flex justify-between">
          <div></div>
          <PrimaryButton
            onClick={() => {
              currentStep.value = 1;
            }}
            className="max-w-fit"
          >
            Get Started
          </PrimaryButton>
        </div>
      </div>
    </Show>
  );
}

function QuestionOne() {
  const shouldShow = computed(() => currentStep.value === 1);
  const subStep = signal(0);

  function QuestionOneYes() {
    const shouldShowSubstep = computed(() => subStep.value === 1);
    return (
      <Show when={shouldShowSubstep}>
        <>
          <h3
            className="text-lg font-bold font-serif leading-6 capitalize mb-3"
            id="modal-title"
          >
            What best describes your experience with reporting?
          </h3>
          <RadioOptionGroup
            options={question1YesSubOptions}
            selected={selectedSubAnswer1}
          />
        </>
      </Show>
    );
  }

  function QuestionOneNo() {
    const shouldShowSubstep = computed(() => subStep.value === 2);
    return (
      <Show when={shouldShowSubstep}>
        <>
          <h3
            className="text-lg font-bold font-serif leading-6 capitalize mb-3"
            id="modal-title"
          >
            What best describes your decision not to report?
          </h3>
          <RadioOptionGroup
            options={question1NoSubOptions}
            selected={selectedSubAnswer1}
          />
        </>
      </Show>
    );
  }

  function QuestionOneSplash() {
    const shouldShowSubstep = computed(() => subStep.value === 0);
    return (
      <Show when={shouldShowSubstep}>
        <>
          <h3
            className="text-lg font-bold font-serif leading-6 capitalize mb-3"
            id="modal-title"
          >
            Have you reported this incident to the police, Title IX, or both?
          </h3>
          <p className="text-sm mb-3">There's no judgement if you haven't!</p>
          <RadioOptionGroup
            options={question1Options}
            selected={selectedAnswer1}
          />
        </>
      </Show>
    );
  }

  return (
    <Show when={shouldShow}>
      <div>
        <QuestionOneSplash />
        <QuestionOneYes />
        <QuestionOneNo />
        <div className="flex items-center justify-between">
          <div />
          <PrimaryButton
            onClick={() => {
              if (selectedAnswer1.value === "I prefer not to answer.") {
                currentStep.value = 2;
              } else {
                // TODO: look into creating another segment on the progress bar?
                if (selectedSubAnswer1.value.length === 0) {
                  if (selectedAnswer1.value === "Yes") {
                    subStep.value = 1;
                  } else {
                    subStep.value = 2;
                  }
                } else {
                  currentStep.value = 2;
                }
              }
            }}
            disabled={selectedAnswer1.value === ""}
            className="max-w-fit"
          >
            Next
          </PrimaryButton>
        </div>
      </div>
    </Show>
  );
}

function QuestionTwo() {
  const shouldShow = computed(() => currentStep.value === 2);

  return (
    <Show when={shouldShow}>
      <div>
        <h3
          class="text-lg font-bold font-serif leading-6 capitalize mb-3"
          id="modal-title"
        >
          Why did you decide to use Callisto's Matching System?
        </h3>
        <p class="text-sm mb-3">(Select all that apply.)</p>
        <CheckboxGroup options={question2Options} selected={selectedAnswer2} />
        <div className="flex items-center justify-between">
          <Button
            onClick={() => {
              currentStep.value = 1;
            }}
            className="max-w-fit"
          >
            Previous
          </Button>
          <PrimaryButton
            onClick={() => {
              currentStep.value = 3;
            }}
            disabled={selectedAnswer2.value.length === 0}
            className="max-w-fit"
          >
            Next
          </PrimaryButton>
        </div>
      </div>
    </Show>
  );
}

function QuestionThree() {
  const shouldShow = computed(() => currentStep.value === 3);

  return (
    <Show when={shouldShow}>
      <div>
        <h3
          class="text-lg font-bold font-serif leading-6 capitalize mb-3"
          id="modal-title"
        >
          After submitting your matching entry, how are you feeling?
        </h3>
        <p class="text-sm mb-3">(Select all that apply.)</p>
        <CheckboxGroup options={question3Options} selected={selectedAnswer3} />
        <div className="flex items-center justify-between">
          <Button
            onClick={() => {
              currentStep.value = 2;
            }}
            className="max-w-fit"
          >
            Previous
          </Button>
          <PrimaryButton
            onClick={() => {
              currentStep.value = 4;
            }}
            disabled={selectedAnswer3.value.length === 0}
            className="max-w-fit"
          >
            Next
          </PrimaryButton>
        </div>
      </div>
    </Show>
  );
}

function QuestionFour() {
  const shouldShow = computed(() => currentStep.value === 4);

  return (
    <Show when={shouldShow}>
      <div>
        <h3
          class="text-lg font-bold font-serif leading-6 capitalize mb-3"
          id="modal-title"
        >
          How likely are you to recommend Callisto to someone after experiencing
          sexual assault?
        </h3>
        <RadioOptionGroup
          options={question4Options}
          selected={selectedAnswer4}
        />
        <div className="flex items-center justify-between">
          <Button
            onClick={() => {
              currentStep.value = 3;
            }}
            className="max-w-fit"
          >
            Previous
          </Button>
          <PrimaryButton
            onClick={() => {
              // Construct the survey responses in a format the database will understand.
              let question1Value = selectedSubAnswer1.peek();
              if (question1Value === "") {
                question1Value = selectedAnswer1.peek();
              }

              const surveyResponses = {
                "Have you reported this incident to the police, Title IX, or both?":
                  question1Value,
                "Why did you decide to use Callisto's Matching System?":
                  selectedAnswer2.peek(),
                "After submitting your matching entry, how are you feeling?":
                  selectedAnswer3.peek(),
                "How likely are you to recommend Callisto to someone after experiencing sexual assault?":
                  selectedAnswer4.peek(),
              };
              submitting.value = true;

              // Wait for just a second to show the spinner before making the call.
              setTimeout(async () => {
                const matchedEntryID =
                  globalState.matchingSurveyLinkedEntry.peek();
                const id = await globalState.client.submitSurvey(
                  // TODO: embed this
                  globalState.client.publicKey,
                  surveyResponses,
                  "post-matching",
                  "", // TODO: get the current campus ID
                  matchedEntryID,
                );

                if (!globalState.client.userData.postMatchingSurveyResponses) {
                  // Save the survey response ID so we can modify it later, and use it to display surveys to folks who
                  // haven't entered one (which is everyone at time of original deployment).
                  globalState.client.userData.postMatchingSurveyResponses = {};
                }
                globalState.client.userData.postMatchingSurveyResponses[
                  matchedEntryID
                ] = id;
                await globalState.client.saveUserData();

                // Reset the survey modal state to its default.
                submitting.value = false;
                globalState.matchingSurveyLinkedEntry.value = "";
              }, 100);
              currentStep.value = 5;
            }}
            disabled={selectedAnswer4.value === ""}
            className="max-w-fit"
          >
            Next
          </PrimaryButton>
        </div>
      </div>
    </Show>
  );
}

function Finished(props: { onComplete: () => void }) {
  const shouldShow = computed(() => currentStep.value === 5);
  return (
    <Show when={shouldShow}>
      <div>
        <div className="text-center">
          <Icons.Heart className="h-16 w-16 text-gold" />
        </div>
        <h3
          className="text-lg font-bold font-serif leading-6 capitalize"
          id="modal-title"
        >
          Thank you for completing this survey!
        </h3>
        <p className="mt-2 text-sm">
          Your feedback will help us to to empower survivors.
        </p>
        <div className="flex justify-between">
          <div></div>
          <PrimaryButton onClick={props.onComplete} className="max-w-fit mt-2">
            View Resources
          </PrimaryButton>
        </div>
      </div>
    </Show>
  );
}

function RadioOption(props: { value: string }) {
  return (
    <Field key={props.value} className="">
      <Radio
        value={props.value}
        className="flex items-center gap-2 my-2 justify-between rounded border border-brand-50 bg-brand-0 p-3 shadow-sm hover:bg-brand-50 data-[checked]:border-brand-0 data-[checked]:bg-brand-50 group"
      >
        <Label className="text-sm font-bold !m-0">{props.value}</Label>
        <Icons.OK className="flex-none hidden w-5 h-5 group-data-[checked]:block" />
      </Radio>
    </Field>
  );
}

function RadioOptionGroup(props: {
  options: string[];
  selected: Signal<string>;
}) {
  return (
    <RadioGroup
      value={props.selected.value}
      onChange={(value) => {
        props.selected.value = value;
      }}
    >
      {props.options.map((item) => (
        <RadioOption value={item} />
      ))}
    </RadioGroup>
  );
}

function CheckboxOption(props: {
  value: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <Field>
      <Checkbox
        checked={props.checked}
        onChange={props.onChange}
        className="flex items-center my-2 gap-2 justify-between rounded border border-brand-50 bg-brand-0 p-3 shadow-sm hover:bg-brand-50 data-[checked]:border-brand-0 data-[checked]:bg-brand-50 group"
      >
        <Label className="text-sm font-bold !m-0">{props.value}</Label>
        <Icons.OK className="flex-none hidden w-5 h-5 group-data-[checked]:block" />
      </Checkbox>
    </Field>
  );
}

function CheckboxGroup(props: {
  options: string[];
  selected: Signal<string[]>;
}) {
  return (
    <div class="overflow-auto">
      {props.options.map((value) => {
        const thisOptionChecked = computed(() =>
          props.selected.value.includes(value),
        );

        return (
          <CheckboxOption
            value={value}
            checked={thisOptionChecked.value}
            onChange={(n) => {
              if (n) {
                props.selected.value = [...props.selected.value, value];
              } else {
                props.selected.value = props.selected.value.filter(
                  (t) => t !== value,
                );
              }
            }}
          />
        );
      })}
    </div>
  );
}
